/**
 * Universal RUL Hooks
 *
 * React Query hooks that work with ANY RUL form URL.
 * No longer tied to specific services - just give it a URL!
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import universalRULService from '@/api/services/universal-rul.service';
import type {
  UniversalRULFormResponse,
  UniversalRULUpdateResponse,
  UniversalRULSubmitResponse,
} from '@/api/services/universal-rul.service';

/**
 * Hook to load a form from any URL
 *
 * @param formUrl - URL to form (e.g., "/formsexamples/rul/forms/text_input_showcase")
 * @param options - React Query options
 * @returns Query result with form data
 *
 * @example
 * const { data: form } = useUniversalRULForm('/formsexamples/rul/forms/text_input_showcase');
 * const { data: form2 } = useUniversalRULForm('/rul/forms/login-example');
 * const { data: form3 } = useUniversalRULForm('/custom-service/forms/my-form');
 */
export const useUniversalRULForm = (
  formUrl: string,
  options?: {
    enabled?: boolean;
    staleTime?: number;
  }
) => {
  return useQuery<UniversalRULFormResponse>({
    queryKey: ['universal-rul-form', formUrl],
    queryFn: () => universalRULService.getForm(formUrl),
    staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes
    enabled: options?.enabled ?? true,
  });
};

/**
 * Hook to update a field in any form
 *
 * @param formUrl - Base form URL
 * @returns Mutation object
 *
 * @example
 * const updateField = useUniversalRULFieldUpdate('/formsexamples/rul/forms/text_input_showcase');
 *
 * // With patchUrlPattern from __form_meta
 * updateField.mutate({
 *   fieldName: 'first_name',
 *   value: 'John',
 *   formState: { departure_airport: 'uuid' },
 *   patchUrlPattern: '/forms/.../field/{field_name}'
 * });
 */
export const useUniversalRULFieldUpdate = (formUrl: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    UniversalRULUpdateResponse,
    Error,
    {
      fieldName: string;
      value: any;
      formState?: Record<string, any>;
      patchUrlPattern?: string;
    }
  >({
    mutationFn: async ({ fieldName, value, formState, patchUrlPattern }) => {
      return universalRULService.updateField(
        formUrl,
        fieldName,
        value,
        formState,
        patchUrlPattern
      );
    },
    onSuccess: (data) => {
      // Update cached form with new state
      // Handle both response formats
      if (data.form) {
        // Flat format (formsexamples) - response includes full form
        queryClient.setQueryData(['universal-rul-form', formUrl], data.form);
      } else if (data.rows) {
        // Hierarchical format - response is the form itself
        queryClient.setQueryData(['universal-rul-form', formUrl], data);
      }
    },
  });
};

/**
 * Hook to submit any form
 *
 * @param formUrl - Base form URL
 * @returns Mutation object
 *
 * @example
 * const submitForm = useUniversalRULSubmit('/formsexamples/rul/forms/text_input_showcase');
 *
 * // Simple submit
 * submitForm.mutate({ data: { first_name: 'John' } });
 *
 * // With custom URL from submit_button.action.url
 * submitForm.mutate({ data: { first_name: 'John' }, submitUrl: '/custom/submit', method: 'POST' });
 */
export const useUniversalRULSubmit = (formUrl: string) => {
  return useMutation<
    UniversalRULSubmitResponse,
    Error,
    { data: Record<string, any>; submitUrl?: string; method?: string }
  >({
    mutationFn: async ({ data, submitUrl, method }) => {
      return universalRULService.submitForm(formUrl, data, submitUrl, method);
    },
  });
};
